import { ResponsiveBar } from '@nivo/bar';
import * as React from 'react'
import { CSVLink } from 'react-csv';
import { useTranslations } from '../../store/translations/hooks';
import { useQueryParam } from '../../hooks/useQueryParam';
import { AppointmentChangeOrigin, AppointmentChangeRecordData, AppointmentChangeType, AppointmentData } from '../../models';
import { FC, useEffect, useMemo, useState } from 'react';
import { TK } from '../../store/translations/translationKeys';
import { AutocompleteTextField } from '../../components/Forms/fields';
import { useBreakpoints } from '../../shared/hooks/useBreakpoints';
import { Alert } from 'reactstrap';
import { serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';
import { shortenName } from 'src/shared/string';

const mapOriginEnum = {
    client: AppointmentChangeOrigin.Client,
    professional: AppointmentChangeOrigin.Professional,
    organization: AppointmentChangeOrigin.Organization,
}

const getCancellationsData = (
    appointments: AppointmentData[],
    cancellations: AppointmentChangeRecordData[],
    unit: 'percentage' | 'count',
    getKey: (a: AppointmentData | AppointmentChangeRecordData) => string,
): Record<string, number> => {

    const noCancellations = cancellations
        .reduce<Record<string, {noCancellations: number, noRebooked: number}>>((acc, curr) => ({
            ...acc,
            [getKey(curr)]: {
                ...(acc[getKey(curr)] ?? {}),
                noCancellations: (acc[getKey(curr)]?.noCancellations ?? 0) + (curr.changeType === AppointmentChangeType.DateChanged ? 0 : 1),
                noRebooked: (acc[getKey(curr)]?.noRebooked ?? 0) + (curr.changeType === AppointmentChangeType.DateChanged ? 1 : 0),
            },
        }), {})

    if (unit === 'count') {
        return Object
            .fromEntries(Object.keys(noCancellations)
            .map<[string, number]>(key => [key, noCancellations[key]?.noCancellations ?? 0]))
    }
    
    const noAppointments = appointments
        .reduce<Record<string, {noAppointmnets: number}>>((acc, curr) => ({
            ...acc,
            [getKey(curr)]: {
                ...(acc[getKey(curr)] ?? {}),
                noAppointmnets: (acc[getKey(curr)]?.noAppointmnets ?? 0) + 1,
            },
        }), {})

    return Object
        .fromEntries([...new Set(Object.keys(noAppointments)), ...new Set(Object.keys(noCancellations))]
        .map<[string, number]>(key => {
            const totalCancelled = noCancellations[key]?.noCancellations ?? 0
            const totalAppointments = (noAppointments[key]?.noAppointmnets ?? 0) + totalCancelled
            return [key, totalAppointments === 0 ? 0 : totalCancelled / totalAppointments]
        }))
}

export const AnalyticsCancellations: FC<{
    appointments: AppointmentData[],
    cancellations: AppointmentChangeRecordData[],
}> = ({
    appointments,
    cancellations,
}) => {
    
    const t = useTranslations()
    const bp = useBreakpoints()
    const generalSettings = useGeneralSettings()

    const [unit, setUnit] = useQueryParam<'count'|'percentage'>('unit', 'percentage')
    const [groupBy, setGroupBy] = useQueryParam<'professional'|'client'|'service'|'reason'>('groupBy', 'professional')
    const [origin, setOrigin] = useQueryParam<'professional'|'client'|'organization'|'all'>('origin', 'all')
    const [filter, setFilter] = useState<string[]>([])
    const isGroupByReason = groupBy === 'reason'
    const currentUnit = isGroupByReason ? 'count' : unit

    useEffect(() => {
        if (isGroupByReason && unit !== 'count') {
            setUnit('count')
        }
    }, [isGroupByReason, setUnit, unit])
    
    const possibleFilterValues = useMemo<string[]>(() => [...new Set([
        ...appointments.map(a => a.professionalName ?? ''),
        ...appointments.map(a => a.clientName ?? ''),
        ...appointments.map(a => serviceName(a.type ?? '', generalSettings)),
        ...cancellations.map(a => a.professionalName ?? ''),
        ...cancellations.map(a => a.clientName ?? ''),
        ...cancellations.map(a => serviceName(a.appointmentType ?? '', generalSettings)),
    ])], [appointments, cancellations, generalSettings])


    const filterFn = (a: AppointmentData | AppointmentChangeRecordData) => !filter.length || !filter.some(v => ![
        a.clientName ?? '',
        a.professionalName ?? '',
        serviceName('type' in a ? (a.type ?? '') : (a.appointmentType ?? ''), generalSettings),
    ].includes(v))

    const cancellationsFiltered = cancellations.filter(a => filterFn(a) && (origin === 'all' || a.reasonOrigin === mapOriginEnum[origin]))
    const appointmentsFiltered = appointments.filter(filterFn)

    const detailedData = getCancellationsData(
        appointmentsFiltered, 
        cancellationsFiltered, 
        currentUnit,
        a => groupBy === 'professional' ? a.professionalName ?? 'unknown'
            : groupBy === 'service' ? serviceName('type' in a ? (a.type ?? '') : (a.appointmentType ?? ''), generalSettings)
            : groupBy === 'reason' ? ('reason' in a ? a.reason ?? t(TK.unknown) : t(TK.none))
            : a.clientName ?? 'unknown',
    )
    
    const totalsData = Object.entries(detailedData)
        .sort((a, b) => a[1] > b[1] ? 1 : -1)
        .map(([k, v]) => ({label: k, [t(TK.value)]: v}))

    const total = totalsData.map(e => e[t(TK.value)]).reduce<number>((total, value) => 
        total + (value as number), 0) / (currentUnit === 'percentage' ? Math.max(totalsData.length, 1) : 1)
    
    const formatValue = (value: string|number) => {
        if (typeof value !== 'number') return value
        return currentUnit === 'percentage' ? `${Math.round(value*1000) / 10} %` : value
    }

    const groupByMap = {
        [t(TK.professional)]: TK.professional,
        [t(TK.client)]: TK.client,
        [t(TK.service)]: TK.service,
        [t(TK.reason)]: TK.reason,
        [TK.professional]: t(TK.professional),
        [TK.client]: t(TK.client),
        [TK.service]: t(TK.service),
        [TK.reason]: t(TK.reason),
    }

    const originMap = {
        [t(TK.all)]: TK.all,
        [t(TK.professional)]: TK.professional,
        [t(TK.client)]: TK.client,
        [t(TK.organization)]: TK.organization,
        [TK.all]: t(TK.all),
        [TK.professional]: t(TK.professional),
        [TK.client]: t(TK.client),
        [TK.organization]: t(TK.organization),
    }

    const unitMap = {
        [t(TK.percentage)]: TK.percentage,
        [t(TK.count)]: TK.count,
        [TK.percentage]: t(TK.percentage),
        [TK.count]: t(TK.count),
    }
    const unitOptions = isGroupByReason
        ? [t(TK.count)]
        : Object.keys(unitMap).slice(0, Object.keys(unitMap).length/2)
    
    return (<>
        <Alert color='secondary' style={{marginTop: 'var(--spacer-S)'}}>{t(TK.cancellationAnalyticsDescription)}</Alert>
        
        <AutocompleteTextField 
            style={{marginTop: 'var(--spacer-S)'}}
            label={t(TK.filter)}
            options={possibleFilterValues}
            multiSelect
            values={filter}
            onValuesChanged={setFilter}
        />
        
        <div style={{display: 'flex', gap: 'var(--spacer-S)', flexDirection: bp.sm ? 'column' : 'row', marginTop: 'var(--spacer-S)'}}>
            <AutocompleteTextField 
                style={{flexGrow: 1}}
                label={t(TK.groupBy)}
                options={Object.keys(groupByMap).slice(0, Object.keys(groupByMap).length/2)}
                value={groupByMap[groupBy]}
                onValueChanged={v => setGroupBy(groupByMap[v] as any ?? 'professional')}
            />

            <AutocompleteTextField 
                style={{flexGrow: 1}}
                label={`${t(TK.filter)} ${t(TK.origin)}`}
                options={Object.keys(originMap).slice(0, Object.keys(originMap).length/2)}
                value={originMap[origin]}
                onValueChanged={v => setOrigin(originMap[v] as any ?? 'all')}
            />

            <AutocompleteTextField 
                style={{flexGrow: 1}}
                label={t(TK.unit)}
                options={unitOptions}
                value={unitMap[currentUnit]}
                onValueChanged={v => setUnit(unitMap[v] as any ?? 'percentage')}
            />
        </div>

        <hr/>
        <h2 style={{textAlign: 'center'}}><b>{t(TK.total)}:</b> {formatValue(total)}</h2>
        <hr/>

        <div style={{height:`${150+totalsData.length*20}px`, width: '100%'}}>
            <ResponsiveBar
                data={totalsData}
                keys={[t(TK.value)]} 
                labelFormat={formatValue}
                tooltipFormat={formatValue}
                labelSkipWidth={35}
                labelSkipHeight={12}
                indexBy="label"
                margin={{ 
                    top: 10, 
                    right: 10, 
                    bottom: 50, 
                    left: totalsData.reduce((curr, item) => curr > (item.label as string).length ? curr : (item.label as string).length, 0) * 5 + 15,
                }}
                padding={0.3}
                colors={{ scheme: 'nivo' }}
                layout="horizontal"
                axisBottom={{
                    legendOffset: 36,
                    legendPosition: 'middle',
                }}
                enableGridX={true}
            />
        </div>
        
        <hr/>

        <h3 style={{marginTop: 'var(--spacer-M)', marginBottom: 'var(--spacer-S)'}}>{t(TK.cancellations)} ({cancellationsFiltered.length})</h3>

        <div style={{overflowX: 'auto', marginBottom: 'var(--spacer-M)'}}>
            <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem'
            }}>
                <thead>
                    <tr style={{
                        backgroundColor: 'var(--bg-light)',
                        borderBottom: `var(--border-medium) solid var(--appLigthGrey)`
                    }}>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.date)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.client)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.appointment)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.type)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.origin)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.reason)}</th>
                        <th style={{
                            padding: 'var(--spacer-S)',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>{t(TK.notes)}</th>
                    </tr>
                </thead>
                <tbody>
                    {cancellationsFiltered.map((cancellation, index) => (
                        <tr key={index} style={{
                            borderBottom: `var(--border-thin) solid var(--appLigthGrey)`,
                            backgroundColor: index % 2 === 0 ? 'white' : 'var(--bg-light)'
                        }}>
                            <td style={{
                                padding: 'var(--spacer-S)',
                                whiteSpace: 'nowrap'
                            }}>
                                {cancellation.timestamp.substring(0, 10)}
                            </td>
                            <td style={{padding: 'var(--spacer-S)', whiteSpace: 'nowrap' }}>
                                {shortenName(cancellation.clientName ?? '-')}
                            </td>
                            <td style={{padding: 'var(--spacer-S)', whiteSpace: 'nowrap' }}>
                                <div>{cancellation.appointmentDate.substring(0, 10)}</div>
                                <div>{serviceName(cancellation.appointmentType ?? '', generalSettings)} {t(TK.with)} {cancellation.professionalName || '-'}</div>
                            </td>
                            <td style={{padding: 'var(--spacer-S)', color: cancellation.changeType === AppointmentChangeType.DateChanged ? 'var(--orange)' : 'var(--red)'}}>
                                {cancellation.changeType === AppointmentChangeType.DateChanged
                                    ? t(TK.rebooked)
                                    : t(TK.cancelled)}
                            </td>
                            <td style={{padding: 'var(--spacer-S)'}}>
                                {cancellation.reasonOrigin === AppointmentChangeOrigin.Client
                                    ? t(TK.client)
                                    : cancellation.reasonOrigin === AppointmentChangeOrigin.Professional
                                    ? t(TK.professional)
                                    : cancellation.reasonOrigin === AppointmentChangeOrigin.Organization
                                    ? t(TK.organization)
                                    : cancellation.reasonOrigin === AppointmentChangeOrigin.Other
                                    ? t(TK.other)
                                    : t(TK.none)}
                            </td>
                            <td style={{padding: 'var(--spacer-S)'}}>
                                {cancellation.reason ?? '-'}
                            </td>
                            <td style={{padding: 'var(--spacer-S)', whiteSpace: 'nowrap' }}>
                                {cancellation.reasonDetails ?? '-'}
                            </td>
                        </tr>
                    ))}
                    {cancellationsFiltered.length === 0 && (
                        <tr>
                            <td
                                colSpan={7}
                                style={{
                                    padding: 'var(--spacer-M)',
                                    textAlign: 'center',
                                    color: 'var(--text-muted)'
                                }}
                            >
                                {t(TK.empty)}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>

        <CSVLink
            data={cancellationsFiltered}
            separator=';'
            target='_blank'
            filename='analysis-export.csv'
        >
            {t(TK.exportData)}
        </CSVLink>
    </>)
}
